import { isoZ, z } from 'my-zod'
import { enumOptionsToObject } from '../../internal/enum-helpers'
import { RentalProductSchemas } from '../rental-product'
import { productLineOptions } from '../../internal'
import { GeneralAddressFragment } from '../../utilities/address'
import { polarisRentalProductAvailability } from './polaris-rental-product-availability'
import { polarisRentalProductPricing } from './polaris-rental-product-pricing'
import { visibilityOptions } from './polaris-rental-product-visibility'

export const polarisRentalProductTypeOptions = ['TimeSlot', 'Hourly', 'Daily'] as const
export const polarisRentalProductTypes = enumOptionsToObject(polarisRentalProductTypeOptions)
export type PolarisRentalProductType = (typeof polarisRentalProductTypeOptions)[number]
export const polarisRentalProductTypeLabels = {
  TimeSlot: 'Time Slot',
  Daily: 'Daily',
  Hourly: 'Hourly'
} satisfies { [key in PolarisRentalProductType]: string }

export const marketingRentalTypeOptions = ['Guided Tour', 'Self-Guided Tour', 'Pickup'] as const
export const marketingRentalTypes = enumOptionsToObject(marketingRentalTypeOptions)
export type MarketingRentalType = (typeof marketingRentalTypeOptions)[number]

export const terrainTypeOptions = ['Desert', 'Forest', 'Mountain', 'Tropical', 'Road', 'Snow', 'Water'] as const
export const terrainTypes = enumOptionsToObject(terrainTypeOptions)
export type TerrainType = (typeof terrainTypeOptions)[number]

export const paymentMechanismOptions = ['External', 'Credits', 'Cash'] as const
export const paymentMechanisms = enumOptionsToObject(paymentMechanismOptions)
export type PaymentMechanism = (typeof paymentMechanismOptions)[number]

export const traileringPartyOptions = ['none', 'customer'] as const
export const traileringParties = enumOptionsToObject(traileringPartyOptions)
export type TraileringParty = (typeof traileringPartyOptions)[number]

export const weatherGuaranteeOptions = ['available', 'unavailable'] as const
export const weatherGuarantees = enumOptionsToObject(weatherGuaranteeOptions)
export type WeatherGuarantee = (typeof weatherGuaranteeOptions)[number]

export const allowedBookingMethodsOptions = ['ADVAndHQ', 'HQOnly', 'External'] as const
export const allowedBookingMethods = enumOptionsToObject(allowedBookingMethodsOptions)
export type AllowedBookingMethods = (typeof allowedBookingMethodsOptions)[number]
export const allowedBookingMethodsLabels = {
  [allowedBookingMethods.ADVAndHQ]: 'ADV and HQ',
  [allowedBookingMethods.HQOnly]: 'HQ Only',
  [allowedBookingMethods.External]: 'External'
}

export const pricingPeriodOptions = ['Hourly', 'Daily', 'Total'] as const
export const pricingPeriods = enumOptionsToObject(pricingPeriodOptions)
export type PricingPeriod = (typeof pricingPeriodOptions)[number]

// If you're adding to this model, please see if sp_restoreVehicleRentalProductVersion
// needs attention too - its our temporary way of being able to restore product versions
// and it knows about all columns right now.
export const polarisRentalProductModel = RentalProductSchemas.rentalProductModel.omit({ platform: true }).augment({
  platform: z.literal('Polaris'),
  productType: z.enum(polarisRentalProductTypeOptions).describe('category of catalog item (ie. pickup, experience'),
  visibility: z.enum(visibilityOptions),
  legacyBookingUrl: z.string().nullable(),
  isDealerDemoRide: z.boolean().nullable(),
  productLines: z
    .enum(productLineOptions)
    .array()
    .nullable()
    .describe('The product lines that this catalog item is compatible with.'),
  customAssetClasses: z.number().array().describe('The custom asset classes that this catalog item is compatible with.'),
  excludeAssetFamilyIds: z
    .string()
    .array()
    .describe(
      'Any asset in specified productLines or custom asset classes can be rented for this product except for assets whose asset family is in this list'
    ),
  latitude: z.number().nullable(),
  longitude: z.number().nullable(),
  addressId: z.string().uuid().nullable(),
  traileringParty: z.enum(traileringPartyOptions),
  landingSlug: z
    .string()
    .max(128)
    .nullable()
    .describe(
      'This will be the portion of the URL used at the end on the marketing site, e.g. https://adventures.polaris.com/adventure/animas-forks-ghost-town/.   Slugs must be unique in the system.  You may wish to set a slug value manually to match its previous value from the old marketing site.'
    ),
  maxBookableDaysInAdvance: z.number().nullable(),
  minBookableHoursInAdvance: z.number().nullable(),
  launchDate: isoZ
    .date()
    .nullable()
    .describe(
      'If Visibility Status = Live and there is a Launch Date, "Coming Soon" will show in CTA button until the Launch Date occurs'
    ),
  showAsJustAddedUntilDate: isoZ
    .date()
    .nullable()
    .describe('The date at which this product will no longer be rendered as just-added, e.g. a newly added item.'),
  overview: z.string().max(3000),
  shortDescription: z.string().max(512).nullable(),
  tags: z.string().array().describe('These are used to group products in interesting ways.'),
  marketingRentalType: z.enum(marketingRentalTypeOptions).nullable().describe('guided or unguided'),
  terrainType: z.enum(terrainTypeOptions).nullable(),
  seoMetaName: z.string().nullable(),
  seoMetaDescription: z.string().nullable(),
  trailDistanceMiles: z.number().nullable(),
  guestRequirements: z.string().max(1000).nullable(),
  whatToBring: z.string().nullable().describe('Enter text for separate What to Bring items on separate lines'),
  whatsIncluded: z.string().nullable().describe('Enter text for separate What is Included items on separate lines'),

  address: GeneralAddressFragment.omit({
    originalAddress: true,
    lastModifiedDate: true,
    deletedDate: true,
    serverLastModifiedDate: true
  }).nullable(),

  productImage: z
    .object({
      url: z.string().min(1),
      alt: z.string().nullable(),
      caption: z.string().max(65).nullable()
    })
    .nullable(),
  landingHeroImage: z
    .object({
      url: z.string().min(1),
      alt: z.string().nullable(),
      caption: z.string().max(65).nullable()
    })
    .nullable(),
  galleryImages: z
    .object({
      url: z.string().min(1),
      alt: z.string().nullable(),
      caption: z.string().max(65).nullable()
    })
    .array()
    .max(10),

  pricing: polarisRentalProductPricing.array(),
  /**
   * If Hourly, these amounts are interpreted as "Price per hour".
   * If Total, these amounts are interpreted as "Price for whole reservation"
   * If Daily, these amounts are interpreted as "Price per day"
   */
  pricingPeriod: z.enum(['Hourly', 'Daily', 'Total']),
  availability: polarisRentalProductAvailability.array(),
  weatherGuarantee: z.enum(weatherGuaranteeOptions),
  allowedBookingMethods: z.enum(allowedBookingMethodsOptions),
  // Does this product allow for usage with Polaris Adventures Select?
  selectAllowedDate: isoZ.instant().nullable(),
  displayPrice: z.number().nullable()
})

export type polarisRentalProductModel = z.infer<typeof polarisRentalProductModel>
